/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { BuilderFormSchemaFactory, FormRootSchema, FormStateSchema } from '@gh/builder-shared';
import { BuilderPermission, CustomFieldType } from '@gh/builder-shared-data';
import {
  FormState,
  MaskedTypes,
  NumberTypes,
  UserContext,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { FormMode, getControl, getControlValue } from '@gh/core-ui';
import { CustomFieldDetails } from '../../resources/custom-field.model';

@Injectable()
export class CustomFieldDetailsFormState extends FormState<CustomFieldDetails> {
  constructor(schemaFactory: BuilderFormSchemaFactory,
              private userContext: UserContext) {
    super(schemaFactory);
  }

  get submitValue(): CustomFieldDetails {
    return {
      ...this.group.getRawValue(),
      placement: getControlValue(this.group, 'placement'),
      digitsLength: getControlValue(this.group, 'digitsLength') || getControlValue(this.group, 'textLength') || void 0,
      dataType: getControlValue(this.group, 'dataType'),
      decimalPlaces: getControlValue(this.group, 'decimalPlaces') || void 0,
      required: getControlValue(this.group, 'required') || false,
      active: getControlValue(this.group, 'active') || true,
      customFieldId: getControlValue(this.group, 'customFieldId') || void 0,
    };
  }

  displayAdditionalFields(value: string): void {
    const textLength = getControl(this.group, 'textLength');
    const digitsLength = getControl(this.group, 'digitsLength');
    const decimalPlaces = getControl(this.group, 'decimalPlaces');

    if (value === CustomFieldType.Text) {
      textLength.enable();
    } else {
      textLength.disable();
      textLength.reset();
    }
    if (value === CustomFieldType.Number) {
      digitsLength.enable();
      decimalPlaces.enable();
    } else {
      digitsLength.disable();
      digitsLength.reset();
      decimalPlaces.disable();
    }
  }

  onTypeCheck(event: MatSelectChange): void {
    this.displayAdditionalFields(event.value);
  }

  protected buildSchema(details: CustomFieldDetails, { root }: FormStateSchema): FormRootSchema {
    const mode = this.userContext.hasPermission(BuilderPermission.Setup.Contacts.CustomFields.EDIT) ?
      FormMode.EditMode
      : FormMode.ViewMode;

    return root({
      optimisticLock: true,
      mode: details.customFieldId ? mode : FormMode.CreateMode,
      enabled: whenCreateOrEditMode(),
      children: {
        customFieldId: void 0,
        placement: { required: true },
        name: { required: true },
        dataType: { required: true },
        required: void 0,
        active: void 0,
        digitsLength: {
          required: true,
          validators: [MaskedTypes.CustomFieldsLength.validator, NumberTypes.CustomFieldLengthNumber.validator],
        },
        textLength: {
          required: true,
          validators: [MaskedTypes.CustomFieldsLength.validator, NumberTypes.CustomFieldLengthText.validator],
        },
        decimalPlaces: { required: true },
      },
    });
  }
}
